/**
 * Initial calendar view (#4121).
 *
 * One policy for the work-order, shift and lift calendars:
 *
 *   1. A view the user picked themselves wins. It is remembered per tenant AND
 *      user, so switching account or workshop in the same browser never lends
 *      one person's choice to another.
 *   2. Otherwise the workshop's `calendarDefaultView` setting (`day` / `week`,
 *      `day` when never set).
 *   3. On mobile an unchosen `week` becomes `day`: the narrow screen cannot
 *      hold a week, and the old per-tab effects did the same. A deliberate
 *      pick still wins everywhere, so week and month stay reachable.
 *
 * The effective view is DERIVED, never copied into state from the setting. A
 * setting that arrives late (`/tenants/me` still loading on first paint) or
 * changes on refetch therefore cannot overwrite a click: the click is stored
 * and the setting is only consulted while nothing is stored.
 */

import { useCallback, useMemo, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTenantSettings, type CalendarDefaultView } from '@/hooks/useTenantSettings';

export type CalendarViewChoice = 'day' | 'week' | 'month';

const VIEW_VALUES: readonly CalendarViewChoice[] = ['day', 'week', 'month'];

/** localStorage keys of the shift and lift calendars (#4121). Pre-#4121 they held one unscoped value. */
export const SHIFT_CALENDAR_VIEW_KEY = 'shiftCalendarView';
export const LIFT_CALENDAR_VIEW_KEY = 'equipmentCalendarView';

export function isCalendarViewChoice(value: unknown): value is CalendarViewChoice {
  return typeof value === 'string' && (VIEW_VALUES as readonly string[]).includes(value);
}

/** `tenantId:userId`, or `anonymous` before sign-in. Persisted view choices are keyed by it. */
export function calendarViewScope(
  user: { id: string; tenantId: string } | null | undefined,
): string {
  return user ? `${user.tenantId}:${user.id}` : 'anonymous';
}

/**
 * The view to show. `explicit` is what the user picked (null = nothing),
 * `defaultView` the workshop setting.
 */
export function resolveCalendarView(input: {
  explicit: CalendarViewChoice | null;
  defaultView: CalendarDefaultView;
  isMobile: boolean;
}): CalendarViewChoice {
  if (input.explicit !== null) return input.explicit;
  if (input.isMobile && input.defaultView === 'week') return 'day';
  return input.defaultView;
}

/** Scope of the signed-in account for view persistence. */
export function useCalendarViewScope(): string {
  const user = useAuthStore((s) => s.user);
  return calendarViewScope(user);
}

/** The workshop's initial calendar view (`day` when unset or still loading). */
export function useCalendarDefaultView(): CalendarDefaultView {
  return useTenantSettings().calendarDefaultView;
}

export interface CalendarViewState {
  view: CalendarViewChoice;
  /** An explicit pick: stored for this tenant and user, and wins over the default. */
  setView: (view: CalendarViewChoice) => void;
}

/** View state of the work-order calendar, kept in the (session) calendar store. */
export function useWorkshopCalendarView(): CalendarViewState {
  const scope = useCalendarViewScope();
  const defaultView = useCalendarDefaultView();
  const isMobile = useIsMobile();
  const storedView = useCalendarStore((s) => s.calendarView);
  const storedScope = useCalendarStore((s) => s.calendarViewScope);
  const storeSetView = useCalendarStore((s) => s.setCalendarView);

  const explicit = storedScope === scope && isCalendarViewChoice(storedView) ? storedView : null;
  const view = resolveCalendarView({ explicit, defaultView, isMobile });
  const setView = useCallback(
    (next: CalendarViewChoice) => { storeSetView(next, scope); },
    [storeSetView, scope],
  );
  return { view, setView };
}

function scopedKey(storageKey: string, scope: string): string {
  return `${storageKey}:${scope}`;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / quota: the choice then lasts for this mount only.
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to clean up.
  }
}

/**
 * Stored explicit choice for `scope`. The pre-#4121 key held one unscoped value
 * that was only ever written by a click, so it is a real choice: the first
 * signed-in account to read it takes it over and the old key is removed, so a
 * second account on the same browser does not inherit it.
 */
export function readStoredCalendarView(
  storageKey: string,
  scope: string,
): CalendarViewChoice | null {
  const scoped = safeGet(scopedKey(storageKey, scope));
  if (isCalendarViewChoice(scoped)) return scoped;

  if (scope === 'anonymous') return null;
  const legacy = safeGet(storageKey);
  if (isCalendarViewChoice(legacy)) {
    safeSet(scopedKey(storageKey, scope), legacy);
    safeRemove(storageKey);
    return legacy;
  }
  return null;
}

/** View state of a calendar that remembers its choice in localStorage (shifts, lifts). */
export function useStoredCalendarView(storageKey: string): CalendarViewState {
  const scope = useCalendarViewScope();
  const defaultView = useCalendarDefaultView();
  const isMobile = useIsMobile();
  // The pick is also held in memory, so it still wins when storage is unavailable.
  const [picked, setPicked] = useState<{ scope: string; view: CalendarViewChoice } | null>(null);

  const stored = useMemo(
    () => readStoredCalendarView(storageKey, scope),
    [storageKey, scope],
  );
  const explicit = picked !== null && picked.scope === scope ? picked.view : stored;
  const view = resolveCalendarView({ explicit, defaultView, isMobile });
  const setView = useCallback(
    (next: CalendarViewChoice) => {
      safeSet(scopedKey(storageKey, scope), next);
      setPicked({ scope, view: next });
    },
    [storageKey, scope],
  );
  return { view, setView };
}
